import React, { useCallback, useEffect, useState } from 'react';
import { postJSON } from 'lib/request';
import AlertDialog, { AlertIconVariant } from 'components/styleguide/alert_dialog/AlertDialog';
import Icon from 'components/styleguide/icon/Icon';

const styles = require('./SubscriptionStatusGate.module.scss');

type ModalKey = 'review' | 'rejected' | 'welcome';

export interface SubscriptionStatusGateProps {
  modal: ModalKey;
  planName: string | null;
  markSeenUrl: string;
  onboardingMeetingUrl: string | null;
}

interface ModalCopy {
  iconName: string;
  iconVariant: AlertIconVariant;
  title: string;
  message: React.ReactNode;
  primaryText: string;
}

function reviewCopy(planName: string | null): ModalCopy {
  const message = planName
    ? `Your ${planName} plan is being reviewed. This usually takes up to 24 business hours. Once approved, you'll get full access and a confirmation email. In the meantime, feel free to look around.`
    : `Your plan is being reviewed. This usually takes up to 24 business hours. Once approved, you'll get full access and a confirmation email. In the meantime, feel free to look around.`;

  return {
    iconName: 'refreshCw',
    iconVariant: 'info',
    title: "We're setting up your account",
    message,
    primaryText: 'Got it, let me explore',
  };
}

function rejectedCopy(planName: string | null): ModalCopy {
  const message = planName
    ? `Unfortunately, we weren't able to approve your ${planName} subscription. Your payment will be fully refunded. We've sent you an email with more details.`
    : `Unfortunately, we weren't able to approve your subscription. Your payment will be fully refunded. We've sent you an email with more details.`;

  return {
    iconName: 'x',
    iconVariant: 'danger',
    title: "Your account wasn't approved",
    message,
    primaryText: 'Got it',
  };
}

function welcomeCopy(planName: string | null): ModalCopy {
  const intro = planName
    ? `Schedule your onboarding session with our team. It's 30 minutes, and it's the best way to make sure you get real results from your ${planName} plan.`
    : `Schedule your onboarding session with our team. It's 30 minutes, and it's the best way to make sure you get real results from your plan.`;

  const message = (
    <>
      <p className={styles.welcomeIntro}>{intro}</p>
      <ul className={styles.welcomeList}>
        <li className={styles.welcomeListItem}>
          <span className={styles.welcomeListIcon} aria-hidden="true">
            <Icon name="check" size="sm" color="company-text-base" />
          </span>
          <span>See how the platform works, start to finish.</span>
        </li>
        <li className={styles.welcomeListItem}>
          <span className={styles.welcomeListIcon} aria-hidden="true">
            <Icon name="check" size="sm" color="company-text-base" />
          </span>
          <span>Get tips on making the most of your features.</span>
        </li>
        <li className={styles.welcomeListItem}>
          <span className={styles.welcomeListIcon} aria-hidden="true">
            <Icon name="check" size="sm" color="company-text-base" />
          </span>
          <span>Meet the team. We&apos;re here whenever you need us.</span>
        </li>
      </ul>
    </>
  );

  return {
    iconName: 'check',
    iconVariant: 'success',
    title: 'Your account was approved',
    message,
    primaryText: 'Book your onboarding session',
  };
}

export default function SubscriptionStatusGate({
  modal,
  planName,
  markSeenUrl,
  onboardingMeetingUrl,
}: SubscriptionStatusGateProps) {
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    postJSON(markSeenUrl);
  }, [markSeenUrl]);

  const close = useCallback(() => setIsOpen(false), []);

  if (modal === 'review') {
    const copy = reviewCopy(planName);
    return (
      <AlertDialog
        isOpen={isOpen}
        iconName={copy.iconName}
        iconVariant={copy.iconVariant}
        title={copy.title}
        message={copy.message}
        primaryText={copy.primaryText}
        onPrimary={close}
      />
    );
  }

  if (modal === 'rejected') {
    const copy = rejectedCopy(planName);
    return (
      <AlertDialog
        isOpen={isOpen}
        iconName={copy.iconName}
        iconVariant={copy.iconVariant}
        title={copy.title}
        message={copy.message}
        primaryText={copy.primaryText}
        onPrimary={close}
      />
    );
  }

  const copy = welcomeCopy(planName);

  const handleBookOnboarding = () => {
    if (onboardingMeetingUrl) {
      window.open(onboardingMeetingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      iconName={copy.iconName}
      iconVariant={copy.iconVariant}
      title={copy.title}
      message={copy.message}
      primaryText={copy.primaryText}
      onPrimary={handleBookOnboarding}
      secondary={{
        prefixText: 'Already scheduled your session?',
        linkText: 'Go to the platform',
        onClick: close,
      }}
    />
  );
}
